import { CSSProperties, FC, MouseEvent, useContext, useEffect, useRef, useState } from "react";
import CarEntity from "../../../../domain/entities/CarEntity";
import FavoriteCarsContext from "../../../../domain/providers/favoriteCars/FavoriteCarsContext";
import UserContext from "../../../../domain/providers/user/UserContext";
import di from "../../../../di/DependencyInjection";
import LikeCarUseCase, { LikeCarUseCaseName } from "../../../../domain/use_cases/car/LikeCarUseCase";
import { trackEvent, type OrigenFavorito } from "../../../../lib/analytics";
import { IS_SCREENS_MODE } from "../../../../config/appMode";
import Icons from "../../assets/Icons";
import { useRequireAuth } from "../authGateModal/useRequireAuth";

interface Props {
    car: CarEntity;
    origen: OrigenFavorito;
    style?: CSSProperties;
}

// Corazón para guardar/quitar un vehículo de favoritos. Sin sesión abre el modal de login y,
// al entrar, guarda el vehículo que se intentó marcar.
const FavoriteButton: FC<Props> = ({ car, origen, style }) => {
    const { favoriteCars } = useContext(FavoriteCarsContext);
    const { user } = useContext(UserContext);
    const { guard, modal } = useRequireAuth("favorito", String(car.id));
    const [guardando, setGuardando] = useState(false);
    const pendiente = useRef(false);
    const esFavorito = favoriteCars.some((c) => c.id === car.id);

    const alternar = async (like: boolean) => {
        setGuardando(true);
        const resultado = await di.get<LikeCarUseCase>(LikeCarUseCaseName).call(car, like);
        setGuardando(false);
        if (resultado._tag === "Left") {
            trackEvent("favorito_fallido", {
                vehiculo_id: String(car.id),
                accion: like ? "agregar" : "eliminar",
                motivo_error: resultado.left.message ?? "desconocido",
            });
            return;
        }
        trackEvent(like ? "favorito_agregado" : "favorito_eliminado", {
            vehiculo_id: String(car.id),
            origen,
            marca: car.brand?.name?.toLowerCase(),
            referencia: car.name?.toLowerCase(),
            anio: Number(car.year) || undefined,
            precio: car.discount_price ?? car.price,
            tipo_vehiculo: car.type?.name?.toLowerCase(),
        });
    };

    // Tras iniciar sesión se espera a que el usuario ya esté en el provider antes de guardar.
    useEffect(() => {
        if (!user || !pendiente.current) return;
        pendiente.current = false;
        alternar(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onClick = (e: MouseEvent) => {
        // Vive dentro del Link de la tarjeta: nunca debe navegar al detalle.
        e.preventDefault();
        e.stopPropagation();
        if (guardando) return;
        if (guard(e, { alExito: () => (pendiente.current = true) })) return;
        alternar(!esFavorito);
    };

    // Las pantallas de las sedes no tienen sesión.
    if (IS_SCREENS_MODE) return null;

    return (
        <>
            <button
                type="button"
                aria-label={esFavorito ? "Quitar de favoritos" : "Guardar en favoritos"}
                aria-pressed={esFavorito}
                onClick={onClick}
                style={{
                    width: 36,
                    height: 36,
                    padding: 0,
                    border: "none",
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.92)",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#ff6600",
                    ...style,
                }}
            >
                {esFavorito ? <Icons.HeartFilled width={22} height={22} /> : <Icons.FavoriteHearth width={22} height={22} />}
            </button>
            {/* El modal es un portal, pero sus clics burbujean por el árbol de React hasta el Link. */}
            <span onClick={(e) => e.stopPropagation()}>{modal}</span>
        </>
    );
};

export default FavoriteButton;
